import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { TargetedSector } from '@/lib/api/threatFeedsTypes';

interface TargetedSectorsChartProps {
    data?: TargetedSector[];
    isLoading?: boolean;
}

// Updated Palette: Flows perfectly from Cyan (largest) to Dark Purple (smallest)
const COLORS = ['#00FFFF', '#3B82F6', '#2563EB', '#6D28D9', '#8B5CF6', '#4C1D95'];

// Custom 4-point star/diamond icon
const DiamondStar = ({ color }: { color: string }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }}>
        <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
    </svg>
);

const TargetedSectorsChart: React.FC<TargetedSectorsChartProps> = ({ data = [], isLoading }) => {

    const topSectors = useMemo(() => {
        return [...data]
            .sort((a, b) => (b.attack_count || 0) - (a.attack_count || 0))
            .slice(0, 6);
    }, [data]);

    // THE MAGIC: Manually calculate angles so we can permanently hardcode the custom widths
    const pieDataWithAngles = useMemo(() => {
        const total = topSectors.reduce((sum, s) => sum + (s.attack_count || 0), 0);
        if (total === 0) return [];

        let currentAngle = 90; // Start at 12 o'clock
        const paddingAngle = 2; // Gap between slices
        const availableDegrees = 360 - (topSectors.length * paddingAngle);

        return topSectors.map((sector, index) => {
            // Calculate the proportional slice size
            const sliceDegrees = ((sector.attack_count || 0) / total) * availableDegrees;
            const startAngle = currentAngle;
            const endAngle = currentAngle - sliceDegrees;

            // Shift the starting position for the next slice
            currentAngle = endAngle - paddingAngle;

            return {
                ...sector,
                startAngle,
                endAngle,
                // Hardcode the step-down width hierarchy (shrinks by 4px each rank)
                outerRadius: 75 - (index * 4)
            };
        });
    }, [topSectors]);

    if (isLoading) {
        return (
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-5 shadow-lg min-h-[300px] flex items-center justify-center font-mono">
                <div className="text-[#3B82F6] text-xs animate-pulse tracking-widest uppercase">
                    Analyzing sectors...
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-5 shadow-lg min-h-[300px] flex items-center justify-center font-mono">
                <div className="text-slate-600 text-xs border border-dashed border-slate-800 rounded-lg p-4">
                    No sector targeting data available.
                </div>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const sectorData = payload[0].payload;
            return (
                <div className="bg-[#0A0B0E]/95 backdrop-blur-md border border-[#1A1D24] p-3 rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.5)] font-mono z-50">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{sectorData.industry_name}</p>
                    <div className="flex items-end gap-2">
                        <span className="text-xl font-bold text-white leading-none">{sectorData.attack_count.toLocaleString()}</span>
                        <span className="text-[10px] uppercase tracking-wide" style={{ color: payload[0].fill }}>Attacks</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg flex flex-col font-mono relative overflow-hidden h-full min-h-[300px]">

            <div className="mb-4">
                <h2 className="text-[14px] font-bold tracking-wide text-white">Targeted Sectors</h2>
            </div>

            <div className="flex-1 flex items-center justify-between gap-4 mt-2 px-2">

                {/* Left Side: Custom Legend List */}
                <div className="flex flex-col justify-center gap-4">
                    {topSectors.map((sector, index) => (
                        <div key={index} className="flex items-center gap-3 text-[13px]">
                            <DiamondStar color={COLORS[index % COLORS.length]} />
                            <span className="text-slate-200 font-medium truncate tracking-wide" title={sector.industry_name}>
                                {sector.industry_name}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Right Side: The Donut Chart */}
                <div className="relative w-[160px] h-[160px] shrink-0 mr-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            {/* Render each slice as its own independent interlocking Pie */}
                            {pieDataWithAngles.map((sector, index) => (
                                <Pie
                                    key={`pie-${index}`}
                                    data={[sector]}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50} // Slightly smaller to ensure the smallest slice still has thickness
                                    outerRadius={sector.outerRadius} // The permanent width hierarchy!
                                    startAngle={sector.startAngle}
                                    endAngle={sector.endAngle}
                                    dataKey="attack_count"
                                    nameKey="industry_name"
                                    stroke="none"
                                    isAnimationActive={false} // Prevents custom angles from glitching during the load spin
                                >
                                    <Cell fill={COLORS[index % COLORS.length]} style={{ filter: `drop-shadow(0px 0px 8px ${COLORS[index % COLORS.length]}90)` }} />
                                </Pie>
                            ))}
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

            </div>
        </div>
    );
};

export default TargetedSectorsChart;